import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import cvData from "../data/cvData";
import projects from "../data/projectsData";
import Seo from "../components/Seo";
import { fadeUp, staggerContainer, staggerItem, viewportOnce, easing } from "../utils/motionVariants";
import "./HomePage.css";

export default function HomePage({ onCalendlyClick = () => {}, onProjectSelect = () => {} }) {
  const highlightProjects = projects.slice(0, 3);
  const topExperience = cvData.experience.slice(0, 2);

  return (
    <div className="home-page">
      <Seo
        path="/"
        description={cvData.summary}
      />
      <motion.section
        className="section section-mist home-overview"
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
        variants={staggerContainer}
      >
        <motion.div className="home-overview-text" variants={staggerItem}>
          <p className="eyebrow">Portfolio Highlights</p>
          <h2>Building reliable products from idea to production.</h2>
          <p>I'm a Full Stack Software Engineer with 2+ years of experience building and shipping production-ready web and 
            mobile applications.
          </p>
          <p>My core experience spans React, Next.js, Laravel, Node.js, Python, and MySQL, with hands-on experience designing 
            APIs, database architectures, frontend applications, third-party integrations, and deployment workflows.
          </p>
          <p>I also build AI-powered features using RAG and LLM APIs, combining traditional software engineering with modern 
            AI capabilities to solve real business problems.
          </p>
          <p>I enjoy owning features end-to-end - from architecture and development to testing, deployment, and production support.
          </p>
          <div className="home-overview-cta">
            <motion.div whileHover={{ scale: 1.04, y: -2 }} whileTap={{ scale: 0.97 }} style={{ display: "inline-block" }}>
              <Link to="/portfolio" className="btn-primary-link">
                View Projects
              </Link>
            </motion.div>
            <motion.button
              type="button"
              className="btn-secondary-link"
              onClick={onCalendlyClick}
              whileHover={{ scale: 1.04, y: -2 }}
              whileTap={{ scale: 0.97 }}
            >
              Let's collaborate
            </motion.button>
          </div>
        </motion.div>
        <motion.div className="home-overview-stats" variants={staggerContainer}>
          {[
            { value: `${projects.length}+`, label: "Products & launches" },
            { value: cvData.experience.length, label: "Professional roles" },
            { value: cvData.references.length, label: "Mentors & references" },
          ].map((stat) => (
            <motion.div
              key={stat.label}
              variants={staggerItem}
              whileHover={{ y: -6, scale: 1.04 }}
              transition={{ duration: 0.25, ease: easing }}
            >
              <strong>{stat.value} </strong><br/>
              <span>{stat.label}</span>
            </motion.div>
          ))}
        </motion.div>
      </motion.section>

      <motion.section
        className="section section-white home-experience"
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
        variants={fadeUp}
      >
        <div className="section-header">
          <h2>Recent Experience</h2>
          <div className="section-underline" />
        </div>
        <motion.div
          className="home-experience-grid"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
        >
          {topExperience.map((role) => (
            <motion.article
              key={role.company}
              className="home-experience-card"
              variants={staggerItem}
              whileHover={{ y: -8, boxShadow: "0 20px 45px rgba(15, 23, 42, 0.12)" }}
              transition={{ duration: 0.25, ease: easing }}
            >
              <div className="badge">{role.period}</div>
              <h3>{role.role}</h3>
              <p className="muted">
                {role.company} &middot; {role.location}
              </p>
              <p>{role.details}</p>
            </motion.article>
          ))}
          <motion.div
            className="home-experience-card link-card"
            variants={staggerItem}
            whileHover={{ y: -8, scale: 1.02 }}
            transition={{ duration: 0.25, ease: easing }}
          >
            <h3>Discover full journey</h3>
            <p>Walk through my complete experience timeline, education, and certifications.</p>
            <Link to="/experience" className="btn-primary-link">
              View Experience
            </Link>
          </motion.div>
        </motion.div>
      </motion.section>

      <motion.section
        className="section section-lilac home-projects"
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
        variants={fadeUp}
      >
        <div className="section-header">
          <h2>Creative Showcase</h2>
          <div className="section-underline" />
        </div>
        <motion.div
          className="home-projects-grid"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
        >
          {highlightProjects.map((project) => (
            <motion.article
              key={project.slug}
              className="home-project-card"
              variants={staggerItem}
              whileHover={{ y: -8, scale: 1.015 }}
              whileTap={{ scale: 0.99 }}
              transition={{ duration: 0.25, ease: easing }}
            >
              <div className="badge">{project.domain?.[0]}</div>
              <h3>{project.title}</h3>
              <p className="muted">{project.oneLiner}</p>
              <motion.button
                type="button"
                className="btn-secondary-link"
                onClick={() => onProjectSelect(project)}
                whileHover={{ x: 4 }}
                transition={{ duration: 0.2 }}
              >
                Explore case study
              </motion.button>
            </motion.article>
          ))}
        </motion.div>
      </motion.section>

      <motion.section
        className="section section-mint home-cta"
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
        variants={fadeUp}
      >
        <div>
          <p className="eyebrow">Next Project</p>
          <h2>Ready to build your next digital experience?</h2>
          <p>
            I can join as a dedicated contributor or partner with your existing team to accelerate delivery,
            refine UX, and ship reliable software.
          </p>
        </div>
        <div className="cta-actions">
          <motion.button
            type="button"
            className="btn-primary-link"
            onClick={onCalendlyClick}
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.97 }}
          >
            Book a call
          </motion.button>
          <motion.div whileHover={{ scale: 1.05, y: -2 }} whileTap={{ scale: 0.97 }} style={{ display: "inline-block" }}>
            <Link to="/portfolio" className="btn-secondary-link">
              Portfolio
            </Link>
          </motion.div>
        </div>
      </motion.section>
    </div>
  );
}
